import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "../../context/ApiContext";
import { IPageBuilder, IComponentNode, PageType } from "../../types/pageBuilder.types";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { PageBuilder as PageBuilderComponent } from "../../components/pageBuilder/PageBuilder";
import { PageBuilderProvider } from "../../context/PageBuilderContext";
import { Card } from "../../components/ui/Card";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Alert } from "../../components/ui/Alert";

// Note: PageBuilderAdmin is deprecated - pages are now managed through CMS Pages
// This component is kept for backward compatibility but should be removed

// Common zone names used across different page types
const COMMON_ZONE_NAMES = ["header", "footer", "sidebar", "content"];

export const PageBuilderAdmin: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const [pages, setPages] = useState<IPageBuilder[]>([]);
 const [loading, setLoading] = useState(true);
 const [showBuilder, setShowBuilder] = useState(false);
 const [editingPage, setEditingPage] = useState<IPageBuilder | null>(null);
 const [searchQuery, setSearchQuery] = useState("");
 const [showZoneManager, setShowZoneManager] = useState(false);
 const [editingZone, setEditingZone] = useState<{ page: IPageBuilder; zoneName: string } | null>(null);
 const [zoneError, setZoneError] = useState<string | null>(null);
 const [newZoneName, setNewZoneName] = useState("");
 const [showCreateForm, setShowCreateForm] = useState(false);
 const [newPageData, setNewPageData] = useState({
  name: "",
  pageType: "static" as PageType,
 });

 useEffect(() => {
  loadPages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []);

 const loadPages = async () => {
  try {
   setLoading(true);
   const response = await api.get("/page-builder/pages?includeUnpublished=true");
   setPages(response.data.pages || []);
  } catch (error) {
   console.error("Error loading pages:", error);
  } finally {
   setLoading(false);
  }
 };

 const handleCreate = () => {
  setShowCreateForm(true);
  setNewPageData({
   name: "",
   pageType: "static",
  });
 };

 const handleCreatePageSubmit = async () => {
  if (!newPageData.name.trim()) {
   setZoneError(t("admin.pageBuilder.nameRequired"));
   return;
  }

  try {
   setZoneError(null);
   const defaultLayout: IComponentNode = {
    id: "root",
    type: "container",
    props: {},
    styles: { default: {} },
    children: [],
   };

   const response =    await api.post("/page-builder/pages", {
    name: newPageData.name,
    // Slug will be auto-generated on the backend from name
    pageType: newPageData.pageType,
    layout: defaultLayout,
   });

   const createdPage = response.data.page;
   setEditingPage(createdPage);
   setShowCreateForm(false);
   setShowBuilder(true);
   await loadPages();
  } catch (error: any) {
   setZoneError(error.response?.data?.error || t("admin.pageBuilder.errors.create"));
  }
 };

 const handleEdit = (page: IPageBuilder) => {
  setEditingPage(page);
  setShowBuilder(true);
 };

 const handleDelete = async (pageId: string) => {
  if (!window.confirm(t("admin.pageBuilder.confirmDelete"))) return;

  try {
   await api.delete(`/page-builder/pages/${pageId}`);
   loadPages();
  } catch (error) {
   console.error("Error deleting page:", error);
  }
 };


 const handleTogglePublish = async (page: IPageBuilder) => {
  try {
   await api.put(`/page-builder/pages/${page._id}`, {
    isPublished: !page.isPublished,
   });
   loadPages();
  } catch (error) {
   console.error("Error toggling publish:", error);
  }
 };

 const handleManageZones = async (page: IPageBuilder) => {
  // Reload page to get latest zone data
  try {
   const response = await api.get(`/page-builder/pages/${page._id}`);
   const updatedPage = response.data.page;
   setEditingPage(updatedPage);
   setShowZoneManager(true);
  } catch (error) {
   console.error("Error loading page:", error);
   setEditingPage(page);
   setShowZoneManager(true);
  }
 };

 const handleEditZone = async (page: IPageBuilder, zoneName: string) => {
  // Reload page to get latest zone data
  try {
   const response = await api.get(`/page-builder/pages/${page._id}`);
   const updatedPage = response.data.page;
   setEditingZone({ page: updatedPage, zoneName });
   setShowZoneManager(false);
  } catch (error) {
   console.error("Error loading page:", error);
   setEditingZone({ page, zoneName });
   setShowZoneManager(false);
  }
 };

 const handleAddZone = async (page: IPageBuilder, zoneName: string) => {
  if (!zoneName.trim()) {
   setZoneError(t("admin.pageZones.zoneNameRequired"));
   return;
  }

  try {
   setZoneError(null);
   const currentZones = page.zones || {};
   const newZone: IComponentNode = {
    id: `zone-${zoneName}-${Date.now()}`,
    type: "container",
    props: {},
    styles: { default: {} },
    children: [],
   };

   const updatedZones = {
    ...currentZones,
    [zoneName]: newZone,
   };

   await api.put(`/page-builder/pages/${page._id}`, {
    zones: updatedZones,
   });

   await loadPages();
   setNewZoneName("");
   setShowZoneManager(false);
  } catch (error: any) {
   setZoneError(error.response?.data?.error || t("admin.pageZones.errors.addZones"));
  }
 };

 const handleDeleteZone = async (page: IPageBuilder, zoneName: string) => {
  if (!window.confirm(`Are you sure you want to delete the "${zoneName}" zone?`)) {
   return;
  }

  try {
   setZoneError(null);
   const currentZones = { ...(page.zones || {}) };
   delete currentZones[zoneName];

   await api.put(`/page-builder/pages/${page._id}`, {
    zones: currentZones,
   });

   loadPages();
   if (editingZone?.page._id === page._id && editingZone.zoneName === zoneName) {
    setEditingZone(null);
   }
  } catch (error: any) {
   setZoneError(error.response?.data?.error || t("admin.pageZones.errors.deleteZone"));
  }
 };

 const handleSaveZone = async (zoneLayout: IComponentNode) => {
  if (!editingZone) return;

  try {
   setZoneError(null);
   const currentZones = { ...(editingZone.page.zones || {}) };
   currentZones[editingZone.zoneName] = zoneLayout;

   await api.put(`/page-builder/pages/${editingZone.page._id}`, {
    zones: currentZones,
   });

   await loadPages();
   setEditingZone(null);
  } catch (error: any) {
   setZoneError(error.response?.data?.error || t("admin.pageZones.errors.saveZone"));
  }
 };

 const getPageZones = (page: IPageBuilder): string[] => {
  return Object.keys(page.zones || {});
 };

 const supportsZones = (pageType: string): boolean => {
  return ["home", "checkout", "product-detail", "product-list", "custom"].includes(pageType);
 };

 const filteredPages = pages.filter((page) =>
  page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  page.slug.toLowerCase().includes(searchQuery.toLowerCase())
 );

 // Zone editor view
 if (editingZone) {
  const zoneContent = editingZone.page.zones?.[editingZone.zoneName] || null;
  return (
   <PageBuilderProvider initialLayout={zoneContent}>
    <div className="fixed inset-0 flex flex-col bg-gray-100 z-50">
     <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
      <div>
       <Heading size="lg">
        Edit Zone: {editingZone.zoneName} ({editingZone.page.name})
       </Heading>
       <p className="text-sm text-gray-500 mt-1">
        This zone will appear on all pages with type: <strong>{editingZone.page.pageType}</strong>
       </p>
       <p className="text-xs text-gray-400 mt-1">
        When a page uses <code className="bg-gray-100 px-1 rounded">&lt;PageZone zoneName="{editingZone.zoneName}" pageType="{editingZone.page.pageType}" /&gt;</code>, 
        it will display this zone content.
       </p>
      </div>
      <Button
       variant="outline"
       onClick={() => {
        setEditingZone(null);
        setShowZoneManager(true);
       }}
      >
       Back to Zones
      </Button>
     </div>
     <div className="flex-1 overflow-hidden">
      <PageBuilderComponent
       initialLayout={zoneContent}
       onCancel={() => {
        setEditingZone(null);
        setShowZoneManager(true);
       }}
       onSave={handleSaveZone}
      />
     </div>
    </div>
   </PageBuilderProvider>
  );
 }

 // Create page form modal
 if (showCreateForm) {
  return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <Card padding="lg" className="max-w-md w-full mx-4">
     <Stack spacing="md">
      <div className="flex items-center justify-between">
       <Heading size="xl">Create New Page</Heading>
       <Button
        variant="outline"
        onClick={() => {
         setShowCreateForm(false);
         setZoneError(null);
        }}
       >
        Cancel
       </Button>
      </div>

      {zoneError && <Alert variant="error">{zoneError}</Alert>}

      <Input
       label={t("admin.pageBuilder.pageName")}
       required
       value={newPageData.name}
       onChange={(e) => {
        setNewPageData({
         ...newPageData,
         name: e.target.value,
         // Slug will be auto-generated on the backend from name
        });
       }}
       placeholder={t("common.placeholders.homePageExample")}
      />

      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Page Type
       </label>
       <select
        value={newPageData.pageType}
        onChange={(e) =>
         setNewPageData({
          ...newPageData,
          pageType: e.target.value as PageType,
         })
        }
        className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
       >
        <option value="static">Static</option>
        <option value="home">Home</option>
        <option value="checkout">Checkout</option>
        <option value="product-detail">Product Detail</option>
        <option value="product-list">Product List</option>
        <option value="custom">Custom</option>
       </select>
       <p className="text-xs text-gray-500 mt-1">
        {supportsZones(newPageData.pageType)
         ? "This page type supports zones (header, footer, sidebar, etc.)"
         : "This page type does not support zones"}
       </p>
      </div>

      <div className="flex gap-2">
       <Button
        variant="primary"
        onClick={handleCreatePageSubmit}
        disabled={!newPageData.name.trim()}
       >
        Create & Edit Layout
       </Button>
      </div>
     </Stack>
    </Card>
   </div>
  );
 }

 // Zone manager modal
 if (showZoneManager && editingPage) {
  const pageZones = getPageZones(editingPage);

  return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <Card padding="lg" className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
     <Stack spacing="md">
      <div className="flex items-center justify-between">
       <Heading size="xl">Manage Zones: {editingPage.name}</Heading>
       <Button
        variant="outline"
        onClick={() => {
         setShowZoneManager(false);
         setEditingPage(null);
         setZoneError(null);
         setNewZoneName("");
        }}
       >
        Close
       </Button>
      </div>

      {zoneError && <Alert variant="error">{zoneError}</Alert>}

      <div>
       <Heading size="md" className="mb-3">Existing Zones</Heading>
       <div className="mb-3 p-3 bg-blue-50 border border-blue-200 ">
        <p className="text-sm text-blue-800">
         <strong>How zones work:</strong> Zones are mapped by <code className="bg-blue-100 px-1 rounded">pageType</code>. 
         When a page uses <code className="bg-blue-100 px-1 rounded">&lt;PageZone zoneName="..." pageType="{editingPage.pageType}" /&gt;</code>, 
         it will display the zone content from this PageBuilder page.
        </p>
        <p className="text-xs text-blue-600 mt-2">
         Example: All pages with <code className="bg-blue-100 px-1 rounded">pageType="{editingPage.pageType}"</code> 
         will use the zones defined here.
        </p>
       </div>
       {pageZones.length === 0 ? (
        <p className="text-gray-500 text-sm">No zones configured yet.</p>
       ) : (
        <div className="space-y-2">
         {pageZones.map((zoneName) => (
          <div
           key={zoneName}
           className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200"
          >
           <div>
            <span className="font-medium text-gray-900">{zoneName}</span>
            <span className="text-sm text-gray-500 ml-2">
             ({editingPage.zones?.[zoneName]?.children?.length || 0} components)
            </span>
            <div className="text-xs text-gray-400 mt-1">
             Used via: <code className="bg-gray-100 px-1 rounded">&lt;PageZone zoneName="{zoneName}" pageType="{editingPage.pageType}" /&gt;</code>
            </div>
           </div>
           <div className="flex gap-2">
            <Button
             variant="secondary"
             size="sm"
             onClick={() => handleEditZone(editingPage, zoneName)}
            >
             Edit
            </Button>
            <Button
             variant="danger"
             size="sm"
             onClick={() => handleDeleteZone(editingPage, zoneName)}
            >
             Delete
            </Button>
           </div>
          </div>
         ))}
        </div>
       )}
      </div>

      <div>
       <Heading size="md" className="mb-3">Add New Zone</Heading>
       <div className="flex gap-2">
        <Input
         placeholder={t("common.placeholders.zoneName")}
         value={newZoneName}
         onChange={(e) => setNewZoneName(e.target.value)}
         onKeyPress={(e) => {
          if (e.key === "Enter") {
           handleAddZone(editingPage, newZoneName);
          }
         }}
        />
        <Button
         variant="primary"
         onClick={async () => {
          await handleAddZone(editingPage, newZoneName);
         }}
         disabled={!newZoneName.trim()}
        >
         Add Zone
        </Button>
       </div>
       <p className="text-xs text-gray-500 mt-2">
        Common zone names: {COMMON_ZONE_NAMES.join(", ")}
       </p>
      </div>
     </Stack>
    </Card>
   </div>
  );
 }

 // Main page builder view
 if (showBuilder) {
  return (
   <PageBuilderProvider initialLayout={editingPage?.layout || null}>
    <PageBuilderComponent
     initialLayout={editingPage?.layout || null}
     onCancel={() => {
      setShowBuilder(false);
      setEditingPage(null);
     }}
     onSave={async (layout: IComponentNode) => {
      if (!editingPage) return;
      try {
       await api.put(`/page-builder/pages/${editingPage._id}`, {
        layout,
       });
       await loadPages();
       setShowBuilder(false);
       setEditingPage(null);
      } catch (error: any) {
       console.error("Error saving page:", error);
      }
     }}
    />
   </PageBuilderProvider>
  );
 }

 if (loading) {
  return <div className="p-8">{t("common.loading")}</div>;
 }

 return (
  <div className="p-8">
   <div className="flex items-center justify-between mb-6">
    <div>
     <h1 className="text-2xl font-bold">{t("admin.pageBuilder.title")}</h1>
     <p className="text-sm text-gray-500 mt-1">
      {t("admin.pageBuilder.description")}
     </p>
    </div>
    <Button onClick={handleCreate} variant="primary">
     {t("admin.pageBuilder.createNewPage")}
    </Button>
   </div>

   <div className="mb-4">
    <Input
     placeholder={t("admin.pageBuilder.searchPages")}
     value={searchQuery}
     onChange={(e) => setSearchQuery(e.target.value)}
    />
   </div>

   {pages.length === 0 && !loading && (
    <Card padding="lg" className="mb-4">
     <div className="text-center py-8">
      <p className="text-gray-500 mb-4">{t("admin.pageBuilder.noPages")}</p>
      <p className="text-sm text-gray-400 mb-4">
       {t("admin.pageBuilder.descriptionNoPages")}
      </p>
      <Button onClick={handleCreate} variant="primary">
       {t("admin.pageBuilder.createFirstPage")}
      </Button>
     </div>
    </Card>
   )}

   <div className="bg-white shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
     <thead className="bg-gray-50">
      <tr>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.pageBuilder.pageName")}
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.pages.slug")}
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.pageBuilder.pageType")}
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.pageZones.zones")}
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.status")}
       </th>
       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {t("admin.actions")}
       </th>
      </tr>
     </thead>
     <tbody className="bg-white divide-y divide-gray-200">
      {filteredPages.map((page) => (
       <tr key={page._id}>
        <td className="px-6 py-4 whitespace-nowrap">
         <button
          onClick={() => handleEdit(page)}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
          type="button"
         >
          {page.name}
         </button>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {page.slug}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         <div className="flex items-center gap-2">
          <span>{page.pageType}</span>
          {supportsZones(page.pageType) && (
           <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 ">
            Zones
           </span>
          )}
         </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
         {supportsZones(page.pageType) ? (
          <span className={getPageZones(page).length > 0 ? "text-green-600 font-medium" : ""}>
           {getPageZones(page).length > 0
            ? t("admin.pageZones.zonesCount", { count: getPageZones(page).length })
            : t("admin.pageZones.noZones")}
          </span>
         ) : (
          <span className="text-gray-400">{t("admin.pageBuilder.notApplicable")}</span>
         )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
         <span
          className={`px-2 inline-flex text-xs leading-5 font-semibold ${
           page.isPublished
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800"
          }`}
         >
          {page.isPublished ? t("admin.pageZones.published") : t("admin.pageZones.draft")}
         </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
         <div className="flex gap-2 flex-wrap">
          <Button
           onClick={() => handleEdit(page)}
           variant="secondary"
           size="sm"
          >
           {t("admin.pageBuilder.editLayout")}
          </Button>
          {supportsZones(page.pageType) && (
           <Button
            onClick={() => handleManageZones(page)}
            variant="secondary"
            size="sm"
           >
            {t("admin.pageZones.manageZones")}
           </Button>
          )}
          <Button
           onClick={() => handleTogglePublish(page)}
           variant={page.isPublished ? "secondary" : "primary"}
           size="sm"
          >
           {page.isPublished ? t("admin.pageBuilder.unpublish") : t("admin.pageBuilder.publish")}
          </Button>
          <Button
           onClick={() => handleDelete(page._id)}
           variant="danger"
           size="sm"
          >
           {t("admin.delete")}
          </Button>
         </div>
        </td>
       </tr>
      ))}
     </tbody>
    </table>
   </div>
  </div>
 );
};

